import { useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import wooCustomApiService from "@/api/services/wooCustomApiService";
import useAppDispatch from "@/hooks/useAppDispatch";
import { handleApiError } from "@/helpers/apiErrorHelper";
import { PaymentMethodId } from "@/constants/enums";
import { type OrderData } from "@/types/checkout";
import { OrderStatus } from "@/types/order";

//#region Types
interface UseOrderPayPageReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  order: OrderData;
}
//#endregion

const { VITE_HEADLESS_SITE_DOMAIN } = import.meta.env;

const useOrderPayPage = (): UseOrderPayPageReturn => {
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const orderKey = searchParams.get("order_key");

  const navigate = useNavigate();
  const location = useLocation();
  const order: OrderData = location.state;

  const appDispatch = useAppDispatch();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const queryParams = `order_key=${order.order_key}&billing_email=${order.billing_address.email}`;
  const orderReceivedPage = `/checkout/order-received/${order.order_id}?${queryParams}`;
  const orderResultApiPath = `/api/checkout/ecpay-payment-result/${order.order_id}?${queryParams}`;

  const handleFormSubmitAndRedirectToECPay = async (formHTML: string) => {
    if (!containerRef || !containerRef.current) return;

    containerRef.current.innerHTML = formHTML;
    const formDOM = containerRef.current.querySelector("form");
    formDOM?.submit();
  };

  const createECPayPaymentOrder = async (signal?: AbortSignal) => {
    if (!orderId || !orderKey || !order) return;
    appDispatch.setPageLoading(true);

    try {
      const { data: formHTML } = await wooCustomApiService.ecpay.payment.createOrder(
        orderId,
        {
          order_key: orderKey,
          client_back_url: `${VITE_HEADLESS_SITE_DOMAIN}${orderReceivedPage}`,
          order_result_url: `${VITE_HEADLESS_SITE_DOMAIN}${orderResultApiPath}`,
        },
        signal,
      );

      handleFormSubmitAndRedirectToECPay(formHTML);
    } catch (error) {
      if (signal?.aborted) return;
      handleApiError(error, appDispatch, "進行付款發生問題，請再重新操作一次", {
        autoClose: false,
      });
      navigate("/cart", { replace: true });
    } finally {
      if (signal?.aborted) return;
      appDispatch.setPageLoading(false);
    }
  };

  const processOrderPaymentByStatus = async (signal: AbortSignal) => {
    try {
      switch (order.status) {
        case OrderStatus.Pending:
          if (order.payment_method === PaymentMethodId.ECPAY_CREDIT) {
            // 訂單已成立：綠界信用卡
            await createECPayPaymentOrder(signal);
            break;
          }
          throw new Error("待付款訂單使用了不支援的付款方式");
        case OrderStatus.Processing:
          if (order.payment_method === PaymentMethodId.COD) {
            // 訂單已成立：轉帳、貨到付款
            navigate(orderReceivedPage, { replace: true });
            break;
          }
          throw new Error("處理中訂單使用了不支援的付款方式");
        case OrderStatus.OnHold:
          // 訂單已成立：轉帳
          if (order.payment_method === PaymentMethodId.BACS) {
            navigate(orderReceivedPage, { replace: true });
            break;
          }
          throw new Error("保留中的訂單使用了不支援的付款方式");
        default:
          throw new Error("訂單狀態錯誤，請重新下單");
      }
    } catch (error) {
      handleApiError(error, appDispatch, "訂單付款處理發生問題，請再重新操作一次", {
        autoClose: false,
      });
      navigate("/cart", { replace: true });
    }
  };

  useEffect(() => {
    // TODO: 從站外綠界金流按回上一頁進來的錯誤處理
    if (!orderId || !orderKey || !order) {
      navigate("/cart", { replace: true });
      return;
    }
    const controller = new AbortController();
    processOrderPaymentByStatus(controller.signal);

    return () => {
      controller.abort();
    };
  }, [orderId, orderKey]);

  return { containerRef, order };
};

export default useOrderPayPage;
